import React, { useState } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'

const BidResponseUploadModal = ({ open, onClose, onSuccess, projectId, url }) => {
  const [name, setName] = useState('')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [duplicate, setDuplicate] = useState(null) // { name, date, packageId }

  const resetForm = () => {
    setName('')
    setFile(null)
    setLoading(false)
    setError('')
    setDuplicate(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleFileChange = (e) => {
    const selected = e.target.files[0]
    if (selected) {
      setFile(selected)
      if (!name) {
        setName(selected.name.replace(/\.[^.]+$/, ''))
      }
    }
  }

  const doUpload = (forceNew = false) => {
    if (!file) {
      setError('Please select a file to upload.')
      return
    }
    if (!name.trim()) {
      setError('Please provide a name for this bid response.')
      return
    }

    setLoading(true)
    setError('')
    setDuplicate(null)

    const formData = new FormData()
    formData.append('bidResponseFile', file)
    formData.append('name', name.trim())
    if (forceNew) {
      formData.append('forceNew', 'true')
    }

    axios.post(`${url}/api/bid-estimation/projects/${projectId}/bid-packages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => {
      setLoading(false)
      resetForm()
      onSuccess(res.data._id || res.data.packageId)
    }).catch(err => {
      setLoading(false)
      if (err.response && err.response.status === 409) {
        const data = err.response.data
        const ep = data.existingPackage || {}
        setDuplicate({
          name: ep.name || data.name || 'Unknown',
          date: ep.createdAt || data.createdAt || null,
          packageId: ep._id || data._id || null
        })
      } else {
        setError(err.response?.data?.message || 'Upload failed. Please try again.')
      }
    })
  }

  const handleOpenExisting = () => {
    if (duplicate && duplicate.packageId) {
      resetForm()
      onSuccess(duplicate.packageId)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch (e) {
      return dateStr
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload Bid Response</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <TextField
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          disabled={loading}
        />

        <div style={{ marginTop: 16, marginBottom: 8 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            File (.xlsx or .pdf)
          </Typography>
          <input
            type="file"
            accept=".xlsx,.pdf"
            onChange={handleFileChange}
            disabled={loading}
            style={{ display: 'block' }}
          />
          {file && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              Selected: {file.name}
            </Typography>
          )}
        </div>

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}

        {duplicate && (
          <div style={{ marginTop: 16, padding: '12px 16px', backgroundColor: '#e3f2fd', borderRadius: 4 }}>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              This file was already imported as <strong>{duplicate.name}</strong>
              {duplicate.date ? ` on ${formatDate(duplicate.date)}` : ''}.
            </Typography>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleOpenExisting}
                disabled={!duplicate.packageId}
              >
                Open Existing
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => doUpload(true)}
                disabled={loading}
              >
                Import as New Version
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => doUpload(false)}
          variant="contained"
          color="primary"
          disabled={loading || !file}
        >
          {loading ? <CircularProgress size={24} /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BidResponseUploadModal
